import { yupResolver } from '@hookform/resolvers/yup';
import { t } from 'i18next';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { Seo } from '../../components/common/Seo';
import { ToastQueueRefContext } from '../../components/common/Toast';
import ErrorForm from '../../components/forms/ErrorForm';
import { connexionSchema, connexionType } from '../../schema/LoginSchema';
import UsersService from '../../services/users.service';
import DoubleAuthSmsModal from '../DashboardApps/DoubleAuthSmsModal';
import LoginForm from './forms/LoginForm';
import TFAForm from './forms/TFAForm';
import UpdateSkemeetPasswordForm from './forms/UpdateSkemeetPasswordForm';

type SkemeetUserInfos = {
  temporaryAccessToken: string;
  firstname: string;
  lastname: string;
  cellphone: string;
};

const LoginComponent = () => {
  const navigate: NavigateFunction = useNavigate();
  const searchQueryParams = useSearchParams()[0];
  const usersService = new UsersService();
  const toastQueueRef = useContext(ToastQueueRefContext);
  const { login } = useAuth();

  const [pageStep, setPageStep] = useState<string>('connexion');
  const [isDoubleAuthEmail, setIsDoubleAuthEmail] = useState<boolean>(false);
  const [redirectUrl, setRedirectUrl] = useState<string | undefined>(undefined);
  const [skemeetUserInfos, setSkemeetUserInfos] = useState<SkemeetUserInfos>({
    temporaryAccessToken: '',
    firstname: '',
    lastname: '',
    cellphone: '',
  });
  const [isDoubleAuthModalOpen, setIsDoubleAuthModalOpen] = useState(false);

  const connexionMethods = useForm<connexionType>({
    resolver: yupResolver(connexionSchema),
    mode: 'onSubmit',
    defaultValues: {
      nickNameOrEmail: '',
      password: '',
    },
  });

  useEffect(() => {
    if (searchQueryParams.get('message') === 'noSession') {
      toastQueueRef?.current?.addToast({
        type: 'ERROR',
        description: t('login.noSession'),
        timer: 5000,
      });
    }
  }, []);

  const redirect = async (check2FA: boolean = true, redirectUrl?: string) => {
    const { data: user } = await usersService.getUserData();
    if (user) {
      login(user);
    }

    if (check2FA) {
      if (user.doubleAuthEmailActivated) return setIsDoubleAuthModalOpen(true);
    }

    if (redirectUrl) {
      window.location.href = redirectUrl;
      return;
    }

    navigate('/dashboard/apps');
  };

  return (
    <>
      <div className='z-10 w-full rounded-lg bg-white p-10 lg:max-w-187.5'>
        {
          {
            connexion: (
              <LoginForm
                setPageStep={setPageStep}
                setSkemeetUserInfos={setSkemeetUserInfos}
                connexionMethods={connexionMethods}
                setIsDoubleAuthEmail={setIsDoubleAuthEmail}
                setRedirectUrl={setRedirectUrl}
                redirect={redirect}
              />
            ),
            connexionStepTwo: (
              <TFAForm
                setPageStep={setPageStep}
                connexionMethods={connexionMethods}
                setIsDoubleAuthEmail={setIsDoubleAuthEmail}
                isDoubleAuthEmail={isDoubleAuthEmail}
                redirect={redirect}
                redirectUrl={redirectUrl}
                setRedirectUrl={setRedirectUrl}
              />
            ),
            updateSkemeetPassword: (
              <UpdateSkemeetPasswordForm skemeetUserInfos={skemeetUserInfos} connexionMethods={connexionMethods} redirect={redirect} />
            ),
            error: <ErrorForm onClick={() => setPageStep('connexion')} />,
          }[pageStep]
        }
      </div>
      <Seo title={'Connexion'} />
      <DoubleAuthSmsModal
        isOpen={isDoubleAuthModalOpen}
        onClose={async () => {
          setIsDoubleAuthModalOpen(false);
          redirect(false, redirectUrl);
        }}
      />
    </>
  );
};

export default LoginComponent;
